"use client";

import {
	type Column,
	type ColumnDef,
	flexRender,
	type Header,
} from "@tanstack/react-table";
import { ArrowDown2, ArrowUp2, Sort } from "iconsax-react";
import { cn } from "../utils";
import { Checkbox } from "./checkbox";
import { Skeleton } from "./skeleton";
import { TableCell, TableRow } from "./table";

const SKELETON_ROW_COUNT = 8;

const SELECT_COLUMN_ID = "__select";
const SELECT_COLUMN_SIZE = 44;

/**
 * Checkbox column prepended automatically when `enableRowSelection` is set, so
 * a page opting into selection never has to hand-write the column.
 */
export function resolveTableColumns<TData, TValue>(
	columns: ColumnDef<TData, TValue>[],
	enableRowSelection: boolean | undefined,
): ColumnDef<TData, TValue>[] {
	if (!enableRowSelection) return columns;
	return [
		buildSelectColumn<TData, TValue>(),
		...columns.filter((column) => column.id !== SELECT_COLUMN_ID),
	];
}

export function buildSelectColumn<TData, TValue>(): ColumnDef<TData, TValue> {
	return {
		cell: ({ row }) => (
			<Checkbox
				aria-label="Select row"
				checked={row.getIsSelected()}
				disabled={!row.getCanSelect()}
				onCheckedChange={(checked) => row.toggleSelected(checked === true)}
				onClick={(event) => event.stopPropagation()}
			/>
		),
		enableHiding: false,
		enableSorting: false,
		header: ({ table }) => (
			<Checkbox
				aria-label="Select all rows on this page"
				checked={
					table.getIsAllPageRowsSelected() ||
					(table.getIsSomePageRowsSelected() && "indeterminate")
				}
				onCheckedChange={(checked) => table.toggleAllPageRowsSelected(checked === true)}
			/>
		),
		id: SELECT_COLUMN_ID,
		size: SELECT_COLUMN_SIZE,
	};
}

export function isPinnedRight<TData, TValue>(column: Column<TData, TValue>): boolean {
	return column.id === "actions" || column.columnDef.meta?.pin === "right";
}

export function shouldTruncate<TData, TValue>(column: Column<TData, TValue>): boolean {
	if (column.columnDef.meta?.truncate === false) return false;
	switch (column.id) {
		case "actions":
		case "image":
		case SELECT_COLUMN_ID:
			return false;
		default:
			return true;
	}
}

export function pinClass<TData, TValue>(column: Column<TData, TValue>): string | undefined {
	if (!isPinnedRight(column)) return undefined;
	return "sticky right-0 z-10 bg-background shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.12)]";
}

export function truncateTitle<TData, TValue>(
	cell: { column: Column<TData, TValue>; getValue: () => unknown },
): string | undefined {
	if (!shouldTruncate(cell.column)) return undefined;
	const value = cell.getValue();
	return typeof value === "string" ? value : undefined;
}

export function SkeletonRows({ columns }: { columns: number }) {
	return Array.from({ length: SKELETON_ROW_COUNT }, (_, row) => (
		<TableRow key={`skeleton-${row}`} className="hover:bg-transparent">
			{Array.from({ length: columns }, (_, col) => (
				<TableCell key={`skeleton-${row}-${col}`}>
					<Skeleton className="h-4 w-full" />
				</TableCell>
			))}
		</TableRow>
	));
}

function ariaSortValue(
	sorted: false | "asc" | "desc",
): "ascending" | "descending" | "none" {
	switch (sorted) {
		case "asc":
			return "ascending";
		case "desc":
			return "descending";
		case false:
			return "none";
	}
}

export function HeaderContent<TData, TValue>({
	header,
}: {
	header: Header<TData, TValue>;
}) {
	const { column } = header;
	const sorted = column.getIsSorted();
	const align = column.columnDef.meta?.align ?? "left";
	const label = flexRender(column.columnDef.header, header.getContext());

	const content = (
		<span
			className={cn(
				"flex min-w-0 items-center gap-1.5",
				align === "right" && "justify-end",
			)}
		>
			<span className="truncate">{label}</span>
		</span>
	);

	if (!column.getCanSort()) return content;

	return (
		<button
			type="button"
			onClick={column.getToggleSortingHandler()}
			className={cn(
				"-mx-2 flex min-w-0 items-center gap-1.5 rounded-sm px-2 py-1 text-muted-foreground transition-colors hover:text-foreground",
				align === "right" && "ms-auto",
			)}
			aria-label={`Sort by ${column.id}`}
			aria-sort={ariaSortValue(sorted)}
		>
			{content}
			<SortIcon sorted={sorted} />
		</button>
	);
}

function SortIcon({ sorted }: { sorted: false | "asc" | "desc" }) {
	switch (sorted) {
		case "asc":
			return <ArrowUp2 className="size-3.5 shrink-0" color="currentColor" variant="Bulk" />;
		case "desc":
			return <ArrowDown2 className="size-3.5 shrink-0" color="currentColor" variant="Bulk" />;
		case false:
			return <Sort className="size-3.5 shrink-0 opacity-40" color="currentColor" variant="Linear" />;
	}
}
